import {
  StudyYearCategoryFullSchema,
  StudyYearCategorySchema,
  StudyYearCategoryWithChildDataSchema,
  StudyYearCategoryCompanySchema,
  DataSchema,
  GetStudyYearCategoryRequest,
  GetStudyYearCategoriesRequest,
  UpdateStudyYearCategoryRequest,
  AddChildCategoryRequest,
  UpdateCategoryForecastsRequest,
  DeleteChildCategoryRequest,
  StudyYearCategoryChildWithData,
  SYCatWithData,
} from '@/types/study-year-category';

import { api } from './index';

class StudyYearCategoriesService {
  // --------------------------------------- V3 - VERSION 3 ------------------------------------------

  /**
   * Get all study year categories with filtering (V3)
   * GET /v3/study-year-categories/
   * NEW IN V3 ✅
   */
  async getStudyYearCategories(
    params?: GetStudyYearCategoriesRequest
  ): Promise<StudyYearCategorySchema[]> {
    try {
      const response = await api.get('/study-year-categories/', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching study year categories (V3):', error);
      throw error;
    }
  }

  /**
   * Get study year categories (new endpoint) (V3)
   * GET /v3/study-year-categories/new
   * NEW IN V3 ✅
   */
  async getStudyYearCategoriesNew(
    params?: GetStudyYearCategoriesRequest
  ): Promise<StudyYearCategorySchema[]> {
    try {
      const response = await api.get('/study-year-categories/new', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching study year categories (new endpoint) (V3):', error);
      throw error;
    }
  }

  /**
   * Get study year category by ID (V3)
   * GET /v3/study-year-categories/{study_year_category_id}
   * NEW IN V3 ✅
   */
  async getStudyYearCategory(
    request: GetStudyYearCategoryRequest
  ): Promise<StudyYearCategoryFullSchema> {
    try {
      const response = await api.get(`/study-year-categories/${request.studyYearCategoryId}`);
      return response.data;
    } catch (error) {
      console.error(
        `Error fetching study year category ${request.studyYearCategoryId} (V3):`,
        error
      );
      throw error;
    }
  }

  /**
   * Update study year category (V3)
   * PATCH /v3/study-year-categories/{study_year_category_id}
   * NEW IN V3 ✅
   */
  async updateStudyYearCategory(
    request: UpdateStudyYearCategoryRequest
  ): Promise<StudyYearCategorySchema> {
    try {
      const { studyYearCategoryId, ...updateData } = request;
      console.log(`Updating study year category ${studyYearCategoryId} (V3):`, updateData);
      const response = await api.patch(
        `/study-year-categories/${studyYearCategoryId}`,
        updateData
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error updating study year category ${request.studyYearCategoryId} (V3): ${error.response?.data?.message || error.message}`,
        error
      );
      
      throw error;
    }
  }

  /**
   * Get study year category forecasts (V3)
   * GET /v3/study-year-categories/{study_year_category_id}/forecasts
   * NEW IN V3 ✅
   */
  async getStudyYearCategoryForecasts(request: GetStudyYearCategoryRequest): Promise<DataSchema[]> {
    try {
      const response = await api.get(
        `/study-year-categories/${request.studyYearCategoryId}/forecasts`
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error fetching study year category forecasts ${request.studyYearCategoryId} (V3):`,
        error
      );
      throw error;
    }
  }

  /**
   * Get study year category data with forecasts (V3)
   * GET /v3/study-year-categories/{study_year_category_id}/data
   * NEW IN V3 ✅
   */
  async getStudyYearCategoryData(
    request: GetStudyYearCategoryRequest
  ): Promise<StudyYearCategoryWithChildDataSchema> {
    try {
      const response = await api.get(
        `/study-year-categories/${request.studyYearCategoryId}/data`
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error fetching study year category data ${request.studyYearCategoryId} (V3):`,
        error
      );
      throw error;
    }
  }

  /**
   * Get child categories (V3)
   * GET /v3/study-year-categories/{study_year_category_id}/children
   * NEW IN V3 ✅
   */
  async getChildCategories(
    request: GetStudyYearCategoryRequest
  ): Promise<StudyYearCategorySchema[]> {
    try {
      const response = await api.get(
        `/study-year-categories/${request.studyYearCategoryId}/children`
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error fetching child categories for ${request.studyYearCategoryId} (V3):`,
        error
      );
      throw error;
    }
  }

  /**
   * Add child category (V3)
   * POST /v3/study-year-categories/{study_year_category_id}/children
   * NEW IN V3 ✅
   */
  async addChildCategory(request: AddChildCategoryRequest): Promise<void> {
    try {
      const { studyYearCategoryId, ...createData } = request;
      await api.post(`/study-year-categories/${studyYearCategoryId}/children`, createData);
    } catch (error) {
      console.error(`Error adding child category to ${request.studyYearCategoryId} (V3):`, error);
      throw error;
    }
  }

  /**
   * Get parent category (V3)
   * GET /v3/study-year-categories/{study_year_category_id}/parent
   * NEW IN V3 ✅
   */
  async getParentCategory(
    request: GetStudyYearCategoryRequest
  ): Promise<StudyYearCategoryFullSchema> {
    try {
      const response = await api.get(
        `/study-year-categories/${request.studyYearCategoryId}/parent`
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error fetching parent category for ${request.studyYearCategoryId} (V3):`,
        error
      );
      throw error;
    }
  }

  /**
   * Get child categories data (V3)
   * GET /v3/study-year-categories/{study_year_category_id}/children/data
   * NEW IN V3 ✅
   */

    async getChildCategoriesData(request: GetStudyYearCategoryRequest): Promise<SYCatWithData[]> {
    try {
      const response = await api.get(
        `/study-year-categories/${request.studyYearCategoryId}/children/forecasts`
      );
      console.log('Fetched child categories data:', response.data);
      return response.data;
    } catch (error) {
      console.error(
        `Error fetching child categories forecasts for ${request.studyYearCategoryId} (V3):`,
        error
      );
      throw error;
    }
  }

  /**
   * Delete child category (V3)
   * POST /v3/study-year-categories/{study_year_category_id}/children/{child_id}/delete
   * NEW IN V3 ✅
   */
  async deleteChildCategory(request: DeleteChildCategoryRequest): Promise<void> {
    try {
      const { studyYearCategoryId, childId, ...deleteData } = request;
      await api.post(
        `/study-year-categories/${studyYearCategoryId}/children/${childId}/delete`,
        deleteData
      );
    } catch (error) {
      console.error(
        `Error deleting child category ${request.childId} from ${request.studyYearCategoryId} (V3):`,
        error
      );
      throw error;
    }
  }

  /**
   * Get category companies (V3)
   * GET /v3/study-year-categories/{study_year_category_id}/companies
   * NEW IN V3 ✅
   */
  async getCategoryCompanies(
    request: GetStudyYearCategoryRequest
  ): Promise<StudyYearCategoryCompanySchema[]> {
    try {
      const response = await api.get(
        `/study-year-categories/${request.studyYearCategoryId}/companies`
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error fetching category companies for ${request.studyYearCategoryId} (V3):`,
        error
      );
      throw error;
    }
  }

  /**
   * Update category forecasts (V3)
   * PATCH /v3/study-year-categories/{study_year_category_id}/children/forecasts
   * NEW IN V3 ✅
   */
  async updateCategoryForecasts(request: UpdateCategoryForecastsRequest): Promise<void> {
    try {
      const { studyYearCategoryId, forecasts } = request;
      await api.patch(
        `/study-year-categories/${studyYearCategoryId}/children/forecasts`,
        forecasts
      );
    } catch (error) {
      console.error(
        `Error updating category forecasts for ${request.studyYearCategoryId} (V3):`,
        error
      );
      throw error;
    }
  }
}

export const studyYearCategoriesService = new StudyYearCategoriesService();
